import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { fmtHours, fmtPercent } from "../lib/calculations";
import { Plus, Trash2 } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
export default function RolesTab() {
  const { t } = useTranslation();
  const {
    estimate, totals, updateRole, addRole, removeRole, formatCurrency,
    setOverheadPercent, setContingencyPercent,
  } = useEstimate();

  const totalEffort = estimate.roles.reduce((s, r) => s + (Number(r.effortPercent) || 0), 0);
  const effortValid = Math.abs(totalEffort - 100) < 0.01;

  return (
    <section className="space-y-4" data-testid="roles-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("roles.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("roles.title")}
            <HelpIconButton anchor="ch3" testId="help-icon-roles" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("roles.description")}
          </p>
        </div>

        <div className="flex items-stretch gap-2 flex-wrap">
          <div
            className={
              "border px-4 py-3 bg-white min-w-[160px] " +
              (effortValid ? "border-gray-300" : "border-[#FF2400]")
            }
            data-testid="effort-total-badge"
          >
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("rolesUi.effortAllocation")}
            </p>
            <p
              className={
                "font-mono text-2xl font-bold leading-none mt-1 " +
                (effortValid ? "text-black" : "text-[#FF2400]")
              }
            >
              {fmtPercent(totalEffort)}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">
              {effortValid ? t("rolesUi.balanced") : t("rolesUi.shouldEqual100")}
            </p>
          </div>
          <button
            data-testid="add-role-btn"
            onClick={addRole}
            className="bg-black text-white hover:bg-[#002FA7] rounded-none px-4 py-2 font-bold transition-colors text-sm inline-flex items-center gap-2 self-stretch"
          >
            <Plus size={14} strokeWidth={2} /> {t("rolesUi.addRole")}
          </button>
        </div>
      </div>

      {/* Cost Adjustments */}
      <div className="bg-white border border-gray-300 p-4" data-testid="cost-adjustments-panel">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
              {t("rolesUi.costAdjustments")}
            </p>
            <h3 className="font-heading text-lg tracking-tight font-bold text-black">
              {t("rolesUi.overheadContingency")}
            </h3>
            <p className="text-sm text-gray-600 mt-1 max-w-xl">
              {t("rolesUi.costAdjustmentsDesc")}
            </p>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
                {t("rolesUi.overheadPercent")}
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={estimate.overheadPercent}
                onChange={(e) => setOverheadPercent(e.target.value)}
                data-testid="overhead-percent-input"
                className="border border-gray-300 rounded-none px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-lg w-28 text-right mt-1"
              />
              <p className="font-mono text-xs text-gray-500 mt-1 text-right" data-testid="overhead-cost-value">
                {formatCurrency(totals.overheadCost)}
              </p>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
                {t("rolesUi.contingencyPercent")}
              </label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={estimate.contingencyPercent}
                onChange={(e) => setContingencyPercent(e.target.value)}
                data-testid="contingency-percent-input"
                className="border border-gray-300 rounded-none px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-lg w-28 text-right mt-1"
              />
              <p className="font-mono text-xs text-gray-500 mt-1 text-right" data-testid="contingency-cost-value">
                {formatCurrency(totals.contingencyCost)}
              </p>
            </div>
            <div className="border-l border-gray-300 pl-6">
              <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 block">
                {t("rolesUi.grandTotal")}
              </label>
              <p className="font-mono text-xl font-bold text-black mt-1" data-testid="grand-total-value">
                {formatCurrency(totals.grandTotal)}
              </p>
              <p className="font-mono text-[10px] text-gray-500 mt-1">
                {t("rolesUi.base")} {formatCurrency(totals.baseCost)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-300 overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse" data-testid="roles-table">
          <thead>
            <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-y border-gray-300">
              <th className="py-3 px-3 w-12">#</th>
              <th className="py-3 px-3">{t("tableHeaders.role")}</th>
              <th className="py-3 px-3 text-right">{t("tableHeaders.hourlyRate")}</th>
              <th className="py-3 px-3 text-right">{t("tableHeaders.effortPercent")}</th>
              <th className="py-3 px-3 text-right">{t("tableHeaders.hours")}</th>
              <th className="py-3 px-3 text-right">{t("tableHeaders.cost")}</th>
              <th className="py-3 px-3 text-right">{t("tableHeaders.pctOfBase")}</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {estimate.roles.map((r, idx) => {
              const br = totals.roleBreakdown[idx];
              return (
                <tr key={idx} className="border-b border-gray-200 hover:bg-[#F9FAFB]">
                  <td className="py-2 px-3 font-mono text-xs text-gray-500">{String(idx + 1).padStart(2, "0")}</td>
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      value={r.name}
                      onChange={(e) => updateRole(idx, "name", e.target.value)}
                      data-testid={`role-${idx}-name`}
                      className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] text-sm w-full font-semibold"
                    />
                  </td>
                  <td className="py-2 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <span className="font-mono text-xs text-gray-500">{estimate.currency?.symbol || "$"}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={r.hourlyRate}
                        onChange={(e) => updateRole(idx, "hourlyRate", e.target.value)}
                        data-testid={`role-${idx}-rate`}
                        className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-sm w-24 text-right"
                      />
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={r.effortPercent}
                      onChange={(e) => updateRole(idx, "effortPercent", e.target.value)}
                      data-testid={`role-${idx}-effort`}
                      className="border border-gray-300 rounded-none px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] font-mono text-sm w-20 text-right"
                    />
                  </td>
                  <td className="py-2 px-3 font-mono text-right text-gray-700" data-testid={`role-${idx}-hours`}>
                    {fmtHours(br?.roleHours || 0)}
                  </td>
                  <td className="py-2 px-3 font-mono text-right text-black font-bold" data-testid={`role-${idx}-cost`}>
                    {formatCurrency(br?.roleCost || 0)}
                  </td>
                  <td className="py-2 px-3 font-mono text-right text-gray-700" data-testid={`role-${idx}-pct`}>
                    {fmtPercent(br?.costPercent || 0)}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={() => removeRole(idx)}
                      data-testid={`role-${idx}-remove`}
                      className="text-gray-500 hover:text-[#FF2400] transition-colors p-1"
                      title={t("rolesUi.removeRole")}
                    >
                      <Trash2 size={14} strokeWidth={2} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-black text-white font-bold uppercase tracking-wider text-[10px]">
              <td colSpan={3} className="py-3 px-3">{t("tableHeaders.baseTotals")}</td>
              <td className="py-3 px-3 font-mono text-right text-sm normal-case" data-testid="roles-effort-sum">
                {fmtPercent(totalEffort)}
              </td>
              <td className="py-3 px-3 font-mono text-right text-sm normal-case" data-testid="roles-hours-sum">
                {fmtHours(totals.totalHours)}
              </td>
              <td className="py-3 px-3 font-mono text-right text-sm normal-case" data-testid="roles-cost-sum">
                {formatCurrency(totals.baseCost)}
              </td>
              <td className="py-3 px-3 font-mono text-right text-sm normal-case">100.0%</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
